import { Injectable } from '@angular/core';
import { act, Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { catchError, mergeMap, tap } from 'rxjs/operators';

/* definitions */
import { authRedirectionPaths } from 'src/app/features/shared/definitions';
import { settings } from 'src/definitions/settings';

/* api services */
import { AuthControllerService } from 'src/app/features/shared/sdk/services';

/* services */
import { TokenService } from 'src/app/features/auth/services';

/* DataStorage interface for ngrx */
import { DataStorage } from 'src/app/features/data-stores/data-storage.interface';

/* router data store actions */
import * as RouterDataStoreActions from '../../router-data-store/state/router-data-store.actions';

/* auth data store actions */
import * as AuthDataStoreActions from './auth-data-store.actions';
import { query } from '@angular/animations';
import { ADMIN_EMAILS, ADMIN_PHONES } from 'src/definitions/custom';

@Injectable()
export class AuthDataStoreEffects {
  constructor(
    private actions$: Actions,
    private store: Store<DataStorage>,
    private authApi: AuthControllerService,
    private tokenService: TokenService,
  ) { }

  register$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthDataStoreActions.Register),
      mergeMap((action) =>
        this.authApi
          .register({
            body: action.payload.user,
          })
          .pipe(
            mergeMap((res) => {
              return [
                {
                  type: AuthDataStoreActions.ActionTypes.RegisterSuccessful,
                  payload: {
                    token: res.token,
                    redirectUrl:
                      action.payload.redirectUrl ??
                      authRedirectionPaths.register
                        .afterRegisterRedirectionPath,
                  },
                },
              ];
            }),
            catchError((error) => {
              return [
                { type: AuthDataStoreActions.ActionTypes.RegisterFailure },
              ];
            }),
          ),
      ),
    ),
  );

  registerSuccessful$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthDataStoreActions.RegisterSuccessful),
      mergeMap((action) => [
        {
          type: AuthDataStoreActions.ActionTypes.SetToken,
          payload: {
            token: action.payload.token,
          },
        },
        {
          type: AuthDataStoreActions.ActionTypes.LoginByToken,
          payload: {
            token: action.payload.token,
            redirectUrl: action.payload.redirectUrl,
          },
        },
      ]),
    ),
  );

  getTokenByCredentials$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthDataStoreActions.GetTokenByCredentials),
      mergeMap((action) => {
        const credentials = {
          ...action.payload.credentials,
          usernameType: settings.usernameType,
        };
        return this.authApi
          .login({
            body: credentials,
          })
          .pipe(
            mergeMap((res) => {
              return [
                {
                  type: AuthDataStoreActions.ActionTypes
                    .GetTokenByCredentialsSuccessful,
                  payload: {
                    token: res.token,
                    redirectUrl: action.payload.redirectUrl,
                  },
                },
              ];
            }),
            catchError((error) => {
              return [
                {
                  type: AuthDataStoreActions.ActionTypes
                    .GetTokenByCredentialsFailure,
                },
              ];
            }),
          );
      }),
    ),
  );

  getOtpByPhoneNumber$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthDataStoreActions.GetOtpByPhoneNumber),
      mergeMap((action) => {
        return this.authApi.requestOTPForLogin({
          body: {
            username: action.payload.username,
            usernameType: action.payload.usernameType,
          },
        })
          .pipe(
            mergeMap((res) => {
              return [
                {
                  type: AuthDataStoreActions.ActionTypes
                    .GetOtpByPhoneNumberSuccessful,
                  payload: {
                    username: action.payload.username,
                    usernameType: action.payload.usernameType,
                  },
                },
              ];
            }),
            catchError((error) => {
              return [
                {
                  type: AuthDataStoreActions.ActionTypes
                    .GetOtpByPhoneNumberFailure,
                },
                {
                  type: RouterDataStoreActions.ActionTypes.Navigate,
                  payload: { path: '/auth/register' }, // newRegister=true
                },
                {
                  type: RouterDataStoreActions.ActionTypes.SetQueryParams,
                  payload: { queryParams: { newRegister: true } }
                },
              ];
            }),
          );
      }),
    ),
  );

  getTokenByOtp$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthDataStoreActions.GetTokenByOtp),
      mergeMap((action) => {
        return this.authApi.loginByOTP({
          body: {
            password: action.payload.password,
            username: action.payload.username,
            usernameType: action.payload.usernameType,
          },
        })
          .pipe(
            mergeMap((res: any) => {
              if (res.token) {
                return [
                  {
                    type: AuthDataStoreActions.ActionTypes
                      .GetTokenByOtpSuccessful,
                    payload: {
                      token: res.token,
                      redirectUrl: action.payload.redirectUrl,
                    },
                  },
                  {
                    type: AuthDataStoreActions.ActionTypes.SetToken,
                    payload: {
                      token: res.token,
                    },
                  },
                  {
                    type: AuthDataStoreActions.ActionTypes.LoginByToken,
                    payload: {
                      token: res.token,
                      redirectUrl: action.payload.redirectUrl,
                    },
                  },
                ];

              }

            }),
            catchError((error) => {
              return [
                {
                  type: AuthDataStoreActions.ActionTypes
                    .GetTokenByOtpFailure,
                },
              ];
            }),
          );
      }),
    ),
  );

  postTokenProcess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthDataStoreActions.GetTokenByCredentialsSuccessful),
      mergeMap((action) => {
        return [
          {
            type: AuthDataStoreActions.ActionTypes.SetToken,
            payload: {
              token: action.payload.token,
            },
          },
          {
            type: AuthDataStoreActions.ActionTypes.LoginByToken,
            payload: {
              token: action.payload.token,
              redirectUrl: action.payload.redirectUrl,
            },
          },
        ];
      }),
    ),
  );

  loginByToken$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthDataStoreActions.LoginByToken),
      mergeMap((action) => {
        const isTokenExist = !!action.payload.token;
        if (isTokenExist) {
          return this.authApi.getUserByToken().pipe(
            mergeMap((user) => {
              return [
                {
                  type: AuthDataStoreActions.ActionTypes.LoggedIn,
                  payload: {
                    token: action.payload.token,
                    user,
                    redirectUrl: action.payload.redirectUrl,
                  },
                },
              ];
            }),
            catchError((error) => {
              return [
                { type: AuthDataStoreActions.ActionTypes.LoginByTokenFailure },
              ];
            }),
          );
        } else {
          return [
            { type: AuthDataStoreActions.ActionTypes.LoginByTokenFailure },
          ];
        }
      }),
    ),
  );

  loginByTokenFailure$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthDataStoreActions.LoginByTokenFailure),
      mergeMap((action) => [{ type: AuthDataStoreActions.ActionTypes.Logout }]),
    ),
  );

  setToken$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AuthDataStoreActions.SetToken),
        tap((action) => {
          const token = action.payload.token;
          this.tokenService.setToken(token);
        }),
      ),
    { dispatch: false },
  );

  clearToken$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AuthDataStoreActions.ClearToken),
        tap((action) => {
          this.tokenService.clearToken();
        }),
      ),
    { dispatch: false },
  );

  loggedIn$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthDataStoreActions.LoggedIn),
      mergeMap((action) => {
        const user = action.payload.user;

        const hasAdminEmail = user?.email && ADMIN_EMAILS.includes(user.email);
        const hasAdminPhone = user?.phoneNumber && ADMIN_PHONES.includes(user.phoneNumber);
        const isAdmin = hasAdminEmail || hasAdminPhone;
        const redirectUrl = isAdmin ? '/tours' : '/users/profile';

        const actions: any[] = [
          {
            type: AuthDataStoreActions.ActionTypes.SetUser,
            payload: { user: action.payload.user },
          },
        ];
        if (redirectUrl) {
          actions.push({
            type: RouterDataStoreActions.ActionTypes.Navigate,
            payload: { path: redirectUrl },
          });
        }
        return actions;
      }),
    ),
  );

  logout$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthDataStoreActions.Logout),
      mergeMap((action) => {
        return [
          { type: AuthDataStoreActions.ActionTypes.ClearToken },
          {
            type: AuthDataStoreActions.ActionTypes.SetUser,
            payload: { user: null },
          },
          {
            type: RouterDataStoreActions.ActionTypes.Navigate,
            payload: { path: '/auth' },
          },
        ];
      }),
    ),
  );

  navigateToLoginPage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthDataStoreActions.NavigateToLoginPage),
      mergeMap((action) => {
        return [
          {
            type: RouterDataStoreActions.ActionTypes.Navigate,
            payload: { path: '/auth/login' },
          },
        ];
      }),
    ),
  );

  navigateToRegisterPage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthDataStoreActions.NavigateToRegisterPage),
      mergeMap((action) => {
        return [
          {
            type: RouterDataStoreActions.ActionTypes.Navigate,
            payload: { path: '/auth/register' },
          },
        ];
      }),
    ),
  );
}
